// Friendly error surface for route loaders / components that throw.
// Mounted by the _app layout's <CatchBoundary> so the sidebar + topbar
// stay visible and the operator can navigate to another section.
//
// Classifies APIError by status so 403 reads as "Access denied" rather
// than the raw "permission denied" string, 401 routes to /login, and
// 404 / 5xx each get their own copy.
import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { APIError, setCSRF } from "@/lib/api";
import { keys } from "@/lib/queries";
import { Button } from "@/components/ui/Button";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

type ErrorKind =
  | "forbidden"
  | "unauthorized"
  | "notfound"
  | "server"
  | "network"
  | "unknown";

interface Classified {
  kind: ErrorKind;
  title: string;
  description: string;
  detail?: string;
}

function classify(err: unknown, t: TFunction): Classified {
  if (err instanceof APIError) {
    if (err.status === 403) {
      return {
        kind: "forbidden",
        title: t("errors.accessDenied"),
        description: t("errors.accessDeniedDescription"),
        detail: err.code && err.code !== "UNKNOWN" ? err.code : undefined,
      };
    }
    if (err.status === 401) {
      return {
        kind: "unauthorized",
        title: t("errors.sessionExpired"),
        description: t("errors.sessionExpiredDescription"),
      };
    }
    if (err.status === 404) {
      return {
        kind: "notfound",
        title: t("errors.notFound"),
        description: t("errors.notFoundDescription"),
        detail: err.code && err.code !== "UNKNOWN" ? err.code : undefined,
      };
    }
    if (err.status >= 500) {
      return {
        kind: "server",
        title: t("errors.server"),
        description: t("errors.serverDescription"),
        detail: `${err.status} ${err.code ?? ""}`.trim(),
      };
    }
    return {
      kind: "unknown",
      title: t("errors.requestFailed"),
      description: err.message || t("errors.requestFailedDescription"),
      detail: `${err.status} ${err.code ?? ""}`.trim(),
    };
  }
  if (err instanceof TypeError && /fetch|network/i.test(err.message)) {
    return {
      kind: "network",
      title: t("errors.network"),
      description: t("errors.networkDescription"),
    };
  }
  if (err instanceof Error) {
    return {
      kind: "unknown",
      title: t("errors.unexpected"),
      description: err.message || t("errors.unexpectedDescription"),
    };
  }
  return {
    kind: "unknown",
    title: t("errors.unexpected"),
    description: t("errors.unexpectedDescription"),
  };
}

export function ErrorPanel({
  error,
  reset,
}: {
  error: unknown;
  reset?: () => void;
}) {
  const { t } = useTranslation();
  const c = classify(error, t);
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Always call reset() before navigating: relying on CatchBoundary's
  // getResetKey alone was leaving the panel stuck on screen when the
  // resetKey hadn't propagated yet, so we clear the boundary state
  // synchronously and then move the URL.
  const goTo = (to: string, search?: Record<string, string>) => {
    reset?.();
    void navigate({ to, search });
  };
  // The session is dead server-side, but the cached `me` query still
  // holds the old admin. /login's beforeLoad reads it via ensureQueryData,
  // which returns stale cached data without refetching, so it would treat
  // us as logged-in and bounce straight back to this panel — the button
  // appears dead. Clear client auth state first (CSRF + the auth query
  // family) so the guard sees a logged-out user and renders /login.
  const signInAgain = () => {
    setCSRF(null);
    // Drop the stale me + perms + factors, then seed a logged-out `me`
    // so /login's guard renders immediately without a server round-trip
    // (and without risk of bouncing on a cached value).
    qc.removeQueries({ queryKey: keys.auth });
    qc.setQueryData(keys.me(), null);
    goTo("/login", {
      redirect: window.location.pathname + window.location.search,
    });
  };
  const Icon =
    c.kind === "forbidden" || c.kind === "unauthorized"
      ? LockIcon
      : c.kind === "notfound"
        ? SearchIcon
        : c.kind === "network"
          ? WifiOffIcon
          : AlertIcon;
  const toneClasses =
    c.kind === "forbidden" || c.kind === "unauthorized"
      ? "border-warning/30 bg-warning/10 text-warning"
      : c.kind === "notfound"
        ? "border-line-2 bg-bg-1 text-t-3"
        : "border-danger/30 bg-danger/10 text-danger";
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-5 rounded-lg border border-line-1 bg-bg-2/60 px-8 py-12 text-center shadow-card">
          <span
            className={`inline-flex h-12 w-12 items-center justify-center rounded-full border ${toneClasses}`}
          >
            <Icon className="h-6 w-6" />
          </span>
          <div className="space-y-1.5">
            <h2 className="font-display text-xl tracking-tight text-t-1">
              {c.title}
            </h2>
            <p className="text-sm leading-relaxed text-t-3">{c.description}</p>
          </div>
          {c.detail && (
            <p className="font-mono text-2xs uppercase tracking-wide text-t-4">
              {c.detail}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
            {c.kind === "unauthorized" ? (
              <Button variant="primary" onClick={signInAgain}>
                {t("errors.signInAgain")}
              </Button>
            ) : (
              <>
                {reset && c.kind !== "forbidden" && c.kind !== "notfound" && (
                  <Button variant="primary" onClick={reset}>
                    {t("common.retry")}
                  </Button>
                )}
                <Button variant="ghost" onClick={() => goTo("/")}>
                  {t("errors.backToDashboard")}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LockIcon(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function AlertIcon(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function SearchIcon(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function WifiOffIcon(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
      <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
      <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
      <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  );
}
